/**
 * Deciding which picked files may be attached to a meeting, and how many were
 * refused for each reason.
 *
 * Attaching a spreadsheet to a scheduled meeting produced a toast reading "Only
 * PDF files can be attached" with the file's own name underneath it, and picking
 * three unsupported files produced three of them. Both came from the same shape:
 * the schedule form validated INSIDE its upload loop, so every refusal was its
 * own message and the file in hand was the obvious thing to put in the body.
 *
 * ## Why this returns counts instead of the rejected files
 *
 * The tempting signature hands back the refused files so the caller can say
 * which ones they were. That is the defect, not the fix. The message states a
 * rule the user can already read above the button, and the file is not on screen
 * to be disambiguated — it never enters the attachment list, so naming it
 * answers a question nobody asked.
 *
 * Returning counts makes that structural: there is no name here for a caller to
 * interpolate, so the regression cannot come back by someone helpfully adding
 * one. What a caller can know is that SOMETHING was refused and why, which is
 * exactly enough to show one message per reason.
 *
 * ## Why invalid files do not consume a slot
 *
 * The cap is applied to the survivors, not to the raw selection. Trimming first
 * — which is what the form used to do — meant a refused file could push a
 * perfectly good PDF past the limit and out of the meeting, so the user was
 * punished twice for one bad pick: a message about the spreadsheet, and a PDF
 * that silently never arrived.
 *
 * ## Why a file already attached is dropped in silence
 *
 * Picking the same PDF twice used to attach it twice, so the meeting carried
 * the same document on two rows and uploaded its bytes again. A repeat pick is
 * not a mistake worth interrupting anyone over — the user asked for that file
 * and that file is attached, which is the outcome they wanted. The absence of a
 * second row IS the feedback, so `duplicates` is counted for tests and callers
 * that want it, and the form deliberately says nothing.
 */

/** The only three things this module reads, so a plain object stands in for a `File`. */
export type PickedFile = {
  name: string
  size: number
  type: string
}

export type AttachmentPick<T> = {
  /** In pick order, already trimmed to the slots available. */
  accepted: T[]
  /** How many were refused for not being a type a meeting can carry. */
  rejectedType: number
  /** How many were refused for exceeding `maxBytes`. */
  rejectedSize: number
  /** How many were already attached. Ignored on purpose — see the header. */
  duplicates: number
  /** Valid files that did not fit under the cap. */
  overCap: number
}

export type AttachmentLimits = {
  /** Attachments the meeting can still take. */
  slotsLeft: number
  /** Inclusive — a file of exactly this size is allowed. */
  maxBytes: number
}

/**
 * What a meeting can carry: files it can present. PDFs as they are; PowerPoint,
 * Word and text through the PDF copy the backend makes on upload.
 */
const ATTACHABLE_MIMES = new Set([
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'text/plain',
])
const ATTACHABLE_EXTENSIONS = new Set(['pdf', 'pptx', 'ppt', 'docx', 'doc', 'txt'])
/** The `accept` attribute for the picker. Only a hint: `isAttachable` decides. */
export const ATTACHABLE_ACCEPT = [...ATTACHABLE_EXTENSIONS].map((e) => `.${e}`).join(',')

/** Types that say nothing about the file, so the name decides. */
const UNKNOWN_MIMES = new Set(['', 'application/octet-stream'])

/**
 * Judged by MIME, as before. The name decides only when the browser reported
 * no type at all — a Windows machine without Office often reports none for a
 * .docx or .pptx. A file whose type names something else (an .exe called
 * invoice.pdf) is still refused.
 */
function isAttachable(file: PickedFile): boolean {
  const mime = file.type.toLowerCase().split(';')[0].trim()
  if (ATTACHABLE_MIMES.has(mime)) return true
  if (!UNKNOWN_MIMES.has(mime)) return false
  const ext = /\.([a-z0-9]{1,5})$/i.exec(file.name)?.[1]?.toLowerCase()
  return !!ext && ATTACHABLE_EXTENSIONS.has(ext)
}

/**
 * Identity of an attachment, for spotting one that is already there.
 *
 * Name AND size, because neither alone holds up: a name on its own refuses a
 * revised document the user re-picked on purpose, and a size on its own collides
 * between unrelated files. The name is case-folded since the same file reached
 * through two paths can differ in case while being the one document.
 */
function identity(file: PickedFile): string {
  return `${file.name.trim().toLowerCase()}\u0000${file.size}`
}

/**
 * Split a selection into what may be attached and tallies of what may not.
 *
 * Type is checked before size so a rejected spreadsheet is reported as the wrong
 * kind of file rather than as one that is too big — the first is the reason the
 * user can act on, and a large file of the wrong kind is not a size problem. A file already
 * attached is settled before either, since it has been through both checks once
 * already and there is nothing left to tell the user about it.
 */
export function partitionPickedAttachments<T extends PickedFile>(
  picked: readonly T[] | null | undefined,
  existing: readonly PickedFile[] | null | undefined,
  limits: AttachmentLimits,
): AttachmentPick<T> {
  const result: AttachmentPick<T> = {
    accepted: [],
    rejectedType: 0,
    rejectedSize: 0,
    duplicates: 0,
    overCap: 0,
  }
  if (!picked) return result

  const slots = Math.max(0, limits.slotsLeft)
  // Seeded with what the meeting already holds, then grown as files are taken,
  // so the same file picked twice in ONE selection collapses to one row too.
  const seen = new Set((existing ?? []).map(identity))

  for (const file of picked) {
    if (!isAttachable(file)) {
      result.rejectedType += 1
      continue
    }
    const key = identity(file)
    if (seen.has(key)) {
      result.duplicates += 1
      continue
    }
    if (file.size > limits.maxBytes) {
      result.rejectedSize += 1
      continue
    }
    if (result.accepted.length >= slots) {
      result.overCap += 1
      continue
    }
    seen.add(key)
    result.accepted.push(file)
  }

  return result
}
